import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  Dialog,
  CircularProgress,
  IconButton,
} from '@mui/material';

const NewInventoryWidget = ({
  colors,
  rows,
  loading,
  fetchUnreviewedByAdvertiser,
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const mainColor = colors?.mainColor || '#623C91';
  const displayRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const count = displayRows.length;

  const navigate = useNavigate();
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());

  const toText = useCallback((val, depth = 0) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string' || typeof val === 'number') {
      const s = String(val);
      return s === '[object Object]' ? '' : s;
    }
    if (depth > 2) return '';

    if (Array.isArray(val)) {
      const joined = val
        .map((v) => toText(v, depth + 1))
        .filter(Boolean)
        .join(', ');
      return joined;
    }

    if (typeof val === 'object') {
      const parts = [];
      const push = (v) => {
        const t = toText(v, depth + 1);
        if (t) parts.push(t);
      };
      push(val.name);
      push(val.location);
      push(val.address);
      push(val.city);
      push(val.state);
      push(val.country);

      const joined = parts.join(', ');
      return joined;
    }

    return '';
  }, []);

  const getDsoName = useCallback(
    (d) =>
      toText(d?.owner_name) ||
      toText(d?.Organization?.name) ||
      toText(d?.organization?.name) ||
      toText(d?.dso_name) ||
      '—',
    [toText]
  );

  const getLocationName = (d) =>
    toText(d?.display_location) ||
    toText(d?.location_name) ||
    toText(d?.location) ||
    toText(d?.address) ||
    toText(d?.city) ||
    '—';

  const getRateName = (d) =>
    toText(d?.rateSchedule?.name) ||
    toText(d?.rate_schedule_name) ||
    toText(d?.rateScheduleName) ||
    '—';

  const getOwnerId = useCallback((d) => {
    const candidates = [
      d?.display_owner_id,
      d?.displayOwnerId,
      d?.owner_id,
      d?.ownerId,
      d?.owner?.id,
      d?.Organization?.id,
      d?.organization?.id,
    ];
    const found = candidates.find((v) => v !== undefined && v !== null && v !== '');
    return found === undefined || found === null ? 'unknown' : String(found);
  }, []);

  const fetchUnreviewedDisplays = useCallback(async () => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    const adv_id = userInfo?.org_id;
    if (!adv_id) return;

    setDetailsLoading(true);
    try {
      await fetchUnreviewedByAdvertiser({ adv_id });
    } finally {
      setDetailsLoading(false);
    }
  }, [fetchUnreviewedByAdvertiser]);

  useEffect(() => {
    fetchUnreviewedDisplays();
  }, [fetchUnreviewedDisplays]);

  const displayGroups = useMemo(() => {
    if (!displayRows.length) return [];
    const map = new Map();
    for (const d of displayRows) {
      const ownerId = getOwnerId(d);
      if (!map.has(ownerId)) map.set(ownerId, []);
      map.get(ownerId).push(d);
    }

    const groups = Array.from(map.entries()).map(([ownerId, items]) => {
      const ownerName = getDsoName(items?.[0]);
      return {
        ownerId,
        ownerName,
        items: (items || []).slice().sort((a, b) => {
          const an = toText(a?.name) || '';
          const bn = toText(b?.name) || '';
          return an.localeCompare(bn);
        }),
      };
    });

    return groups.sort((a, b) => a.ownerName.localeCompare(b.ownerName));
  }, [displayRows, getOwnerId, getDsoName, toText]);

  useEffect(() => {
    if (!fullOpen) return;
    if (!displayGroups.length) {
      setExpandedGroupIds(new Set());
      return;
    }
    setExpandedGroupIds(new Set([displayGroups[0].ownerId]));
  }, [fullOpen, displayGroups]);

  const toggleGroup = useCallback((ownerId) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(ownerId)) next.delete(ownerId);
      else next.add(ownerId);
      return next;
    });
  }, []);

  const expandGroup = useCallback((ownerId) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      next.add(ownerId);
      return next;
    });
  }, []);

  const goToActiveCampaign = useCallback(
    (ownerId) => {
      expandGroup(ownerId);
      setFullOpen(false);
      setOpen(false);
      navigate('/campaigns', { state: { fromNewInventory: true, ownerId } });
    },
    [expandGroup, navigate]
  );

  const handleOpen = async () => {
    await fetchUnreviewedDisplays();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFullOpen = async () => {
    await fetchUnreviewedDisplays();
    setFullOpen(true);
  };

  const handleFullClose = async () => {
    setFullOpen(false);
  };

  const topFive = displayRows.slice(0, 5);

  return (
    <>
      <div
        ref={anchorRef}
        className="flex items-center gap-2 px-2 py-1 rounded-xl border cursor-pointer select-none"
        style={{
          borderColor: `${mainColor}35`,
          background: `${mainColor}08`,
          opacity: count > 0 ? 1 : 0.85,
        }}
        onClick={handleOpen}
        title="New inventory available"
      >
        <Inventory2OutlinedIcon style={{ color: mainColor, fontSize: 20 }} />
        <div className="text-sm font-semibold" style={{ color: mainColor }}>
          New inventory
        </div>
        <div
          className="text-xs font-bold px-2 py-0.5 rounded-full"
          style={{ background: mainColor, color: '#fff' }}
        >
          {count > 99 ? '99+' : count}
        </div>
      </div>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          style: {
            width: 340,
            borderRadius: 16,
            border: `1px solid ${mainColor}25`,
            overflow: 'hidden',
          },
        }}
      >
        <div className="px-3 py-2 flex items-center justify-between" style={{ background: `${mainColor}08` }}>
          <div className="font-bold" style={{ color: mainColor }}>New inventory</div>
          <div className="flex items-center gap-1">
            {count > 0 && (
              <button
                type="button"
                className="px-3 py-1 rounded-xl text-sm font-semibold border"
                style={{
                  borderColor: `${mainColor}35`,
                  color: mainColor,
                  background: `${mainColor}08`,
                }}
                onClick={handleFullOpen}
                title="Review displays"
              >
                Review Displays
              </button>
            )}
            <IconButton size="small" onClick={handleClose} title="Close">
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </div>
        </div>

        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {(detailsLoading || loading) ? (
            <div className="p-4 flex items-center gap-2 text-sm text-gray-600">
              <CircularProgress size={18} /> Loading…
            </div>
          ) : topFive.length === 0 ? (
            <div className="p-5 flex flex-col items-center justify-center text-center">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: `${mainColor}12`, color: mainColor }}
              >
                <Inventory2OutlinedIcon style={{ fontSize: 20 }} />
              </div>
              <div className="mt-2 text-sm font-semibold text-gray-800">
                No New Inventory Added Yet
              </div>
              <div className="text-xs text-gray-500">
                New displays will appear here once they are added.
              </div>
            </div>
          ) : (
            topFive.map((d) => (
              <div key={d?.id} className="px-3 py-2 border-t">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-gray-900 truncate" title={d?.name || ''}>
                    {d?.name || 'Unnamed display'}
                  </div>
                  <div className="text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                    style={{ borderColor: `${mainColor}22`, color: mainColor, background: `${mainColor}08` }}
                    title={getDsoName(d)}
                  >
                    {getDsoName(d)}
                  </div>
                </div>
                <div className="text-xs text-gray-600 truncate" title={getLocationName(d)}>
                  {getLocationName(d)}
                </div>
                <div className="text-[11px] text-gray-600 truncate" title={getRateName(d)}>
                  Rate: {getRateName(d)}
                </div>
              </div>
            ))
          )}
        </div>
      </Popover>

      <Dialog open={fullOpen} onClose={handleFullClose} maxWidth="md" fullWidth>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-bold" style={{ color: mainColor }}>
                New inventory details
              </div>
              <div className="text-sm text-gray-700">
                Review newly added displays.
              </div>
            </div>
            <IconButton onClick={handleFullClose} title="Close">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="mt-4 rounded-xl border bg-white overflow-hidden" style={{ borderColor: `${mainColor}22` }}>
            {(detailsLoading || loading) ? (
              <div className="p-6 flex items-center gap-2 text-sm text-gray-600">
                <CircularProgress size={18} /> Loading…
              </div>
            ) : displayRows.length === 0 ? (
              <div className="p-8 flex flex-col items-center justify-center text-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: `${mainColor}12`, color: mainColor }}
                >
                  <Inventory2OutlinedIcon style={{ fontSize: 24 }} />
                </div>
                <div className="mt-3 text-base font-semibold text-gray-800">
                  No New Inventory Added Yet
                </div>
                <div className="text-sm text-gray-500">
                  New displays will show up here when they are available.
                </div>
              </div>
            ) : (
              <div className="max-h-[52vh] overflow-y-auto p-3">
                {displayGroups.map((g) => {
                  const isOpen = expandedGroupIds.has(g.ownerId);
                  return (
                    <div
                      key={g.ownerId}
                      className="mb-3 last:mb-0 rounded-xl border bg-white"
                      style={{ borderColor: `${mainColor}20` }}
                    >
                      <div
                        className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer select-none"
                        onClick={() => toggleGroup(g.ownerId)}
                        style={{
                          background: isOpen ? `${mainColor}10` : 'transparent',
                        }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ background: `${mainColor}10`, color: mainColor, border: `1px solid ${mainColor}22` }}
                          >
                            {isOpen ? (
                              <KeyboardArrowUpRoundedIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownRoundedIcon fontSize="small" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate" title={g.ownerName}>
                              {g.ownerName || '—'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                            style={{
                              borderColor: `${mainColor}22`,
                              color: mainColor,
                              background: `${mainColor}08`,
                            }}
                          >
                            {g.items.length} display(s)
                          </div>
                        </div>
                      </div>

                      {!isOpen && (
                        <div className="px-3 pb-3 pt-0">
                          <div className="text-xs text-gray-700 leading-snug">
                            <span className="font-semibold" style={{ color: mainColor }}>
                              New displays
                            </span>{' '}
                            have been added by{' '}
                            <span className="font-semibold" style={{ color: mainColor }}>
                              {g.ownerName || '—'}
                            </span>
                            . Would you like to add them to your active campaign?
                          </div>
                          <div className="mt-2 flex justify-end">
                            <button
                              type="button"
                              className="px-3 py-1.5 rounded-lg text-sm font-semibold border"
                              style={{
                                borderColor: `${mainColor}55`,
                                color: mainColor,
                                background: `${mainColor}08`,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                goToActiveCampaign(g.ownerId);
                              }}
                            >
                              Continue to expand campaign
                            </button>
                          </div>
                        </div>
                      )}

                      {isOpen && (
                        <div className="p-3 pt-0">
                          <div className="flex justify-end mt-2 mb-3">
                            <button
                              type="button"
                              className="px-3 py-1.5 rounded-lg text-sm font-semibold border"
                              style={{
                                borderColor: `${mainColor}55`,
                                color: mainColor,
                                background: `${mainColor}08`,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                goToActiveCampaign(g.ownerId);
                              }}
                            >
                              Continue to expand campaign
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {g.items.map((d) => (
                              <div
                                key={d?.id}
                                className="rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow"
                                style={{ borderColor: `${mainColor}18` }}
                              >
                                <div className="p-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div
                                        className="text-sm font-bold text-gray-900 truncate"
                                        title={d?.name || ''}
                                      >
                                        {d?.name || 'Unnamed display'}
                                      </div>
                                      <div
                                        className="text-xs text-gray-600 truncate mt-1"
                                        title={getLocationName(d)}
                                      >
                                        {getLocationName(d)}
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                      <div
                                        className="text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                                        style={{
                                          borderColor: `${mainColor}22`,
                                          color: mainColor,
                                          background: `${mainColor}08`,
                                        }}
                                        title={getRateName(d)}
                                      >
                                        {getRateName(d)}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <div
                                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full border truncate"
                                      style={{
                                        borderColor: `${mainColor}22`,
                                        color: '#334155',
                                        background: '#F8FAFC',
                                      }}
                                      title={getDsoName(d)}
                                    >
                                      DSO: {getDsoName(d)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              className="px-3 py-2 rounded-lg text-sm font-semibold border"
              style={{ borderColor: `${mainColor}55`, color: mainColor }}
              onClick={handleFullClose}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

const mapState = (state) => ({
  colors: state.theme.colors,
  rows: state.NewInventory?.rows || [],
  loading: state.NewInventory?.loading || false,
});

const mapDispatch = (dispatch) => ({
  fetchUnreviewedByAdvertiser: (data) =>
    dispatch.NewInventory.fetchUnreviewedByAdvertiser(data),
});

export default connect(mapState, mapDispatch)(NewInventoryWidget);

